import { useGameStore, PlayerRole } from '../stores/gameStore'

const ROLES = [
  { id: 'flux', name: 'FLUX', color: '#00FF66' },
  { id: 'logic', name: 'LOGIC', color: '#4488FF' },
  { id: 'spark', name: 'SPARK', color: '#FF8844' },
  { id: 'echo', name: 'ECHO', color: '#AA44FF' },
] as const

export default function WaitingRoom() {
  const {
    players,
    mySessionId,
    selectedRole,
    isHost,
    error,
    leaveRoom,
    toggleReady,
    startGame,
    changeRole,
  } = useGameStore()

  const playerList = Object.values(players)
  const myPlayer = playerList.find(p => p.sessionId === mySessionId)
  const allReady = playerList.length > 0 && playerList.every(p => p.isReady)
  const takenRoles = new Set(playerList.map(p => p.role))

  const canStart = isHost && allReady

  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-b from-emerald-dark to-gray-900 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-emerald-neon">Poczekalnia</h1>
          <p className="text-gray-400 text-sm">
            Oczekiwanie na graczy ({playerList.length}/4)
          </p>
        </div>
        <button
          onClick={leaveRoom}
          className="px-4 py-2 text-gray-400 hover:text-red-400 transition-colors"
        >
          Opusc
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}

      <div className="flex-1 flex gap-8">
        {/* Player list */}
        <div className="flex-1">
          <h3 className="text-lg font-bold text-white mb-4">Gracze</h3>
          <div className="space-y-3">
            {playerList.map((player, index) => (
              <div
                key={player.sessionId}
                className={`p-4 rounded-lg border-2 flex items-center justify-between transition-all
                  ${player.isReady ? 'border-emerald-neon bg-emerald-neon/10' : 'border-gray-700 bg-gray-800/50'}`}
              >
                <div className="flex items-center gap-3">
                  {/* Role badge */}
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-gray-900"
                    style={{ backgroundColor: getRoleColor(player.role) }}
                  >
                    {player.role.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-white flex items-center gap-2">
                      {player.nickname}
                      {player.sessionId === mySessionId && (
                        <span className="text-xs text-gray-400">(Ty)</span>
                      )}
                      {index === 0 && (
                        <span className="text-xs text-yellow-500">HOST</span>
                      )}
                    </div>
                    <div className="text-sm" style={{ color: getRoleColor(player.role) }}>
                      {player.role.toUpperCase()}
                    </div>
                  </div>
                </div>
                <div className={`text-sm ${player.isReady ? 'text-emerald-neon' : 'text-gray-500'}`}>
                  {player.isReady ? 'Gotowy' : 'Oczekuje'}
                </div>
              </div>
            ))}

            {/* Empty slots */}
            {Array.from({ length: 4 - playerList.length }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="p-4 rounded-lg border-2 border-dashed border-gray-700 text-gray-600 text-center"
              >
                Wolne miejsce
              </div>
            ))}
          </div>
        </div>

        {/* Role selection */}
        <div className="w-64">
          <h3 className="text-lg font-bold text-white mb-4">Zmien role</h3>
          <div className="space-y-2">
            {ROLES.map((role) => {
              const isTaken = takenRoles.has(role.id) && selectedRole !== role.id
              const isSelected = selectedRole === role.id

              return (
                <button
                  key={role.id}
                  onClick={() => !isTaken && changeRole(role.id as PlayerRole)}
                  disabled={isTaken}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all
                    ${isSelected
                      ? 'border-current neon-border'
                      : isTaken
                        ? 'border-gray-800 bg-gray-800/30 opacity-50 cursor-not-allowed'
                        : 'border-gray-700 hover:border-gray-500'
                    }`}
                  style={{ color: isSelected ? role.color : undefined }}
                >
                  <div className="font-bold" style={{ color: role.color }}>
                    {role.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {isTaken ? 'Zajeta' : isSelected ? 'Twoja rola' : 'Dostepna'}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="mt-8 flex gap-4 justify-center">
        <button
          onClick={toggleReady}
          className={`px-8 py-3 font-bold rounded-lg transition-colors
            ${myPlayer?.isReady
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-emerald-neon text-gray-900 hover:bg-emerald-400'
            }`}
        >
          {myPlayer?.isReady ? 'Nie jestem gotowy' : 'Jestem gotowy'}
        </button>

        {isHost && (
          <button
            onClick={startGame}
            disabled={!canStart}
            className="px-8 py-3 bg-yellow-500 text-gray-900 font-bold rounded-lg
                       hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {canStart ? 'Rozpocznij gre!' : 'Czekam na graczy...'}
          </button>
        )}
      </div>

      {!isHost && allReady && (
        <p className="text-center text-gray-400 mt-4">
          Oczekiwanie na hosta...
        </p>
      )}
    </div>
  )
}

function getRoleColor(role: string): string {
  const colors: Record<string, string> = {
    flux: '#00FF66',
    logic: '#4488FF',
    spark: '#FF8844',
    echo: '#AA44FF',
  }
  return colors[role] || '#ffffff'
}
